import { useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { ProcessAnswerFuncType } from 'src/features/generalForms/fillingModal/GeneralFormFillingModal.t';
import { useGeneralFormFillingModal } from 'src/features/generalForms/fillingModal/useGeneralFormFillingModal';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { useStudy } from 'src/helpers/hooks/useStudy';
import {
  IScoreResult,
  VersionType,
  ScoreResult,
  FileParameter,
  MonitoringFormProcessingDto,
} from 'src/services/api/api-client';
import {
  getMonitoringFormConfig,
  getMonitoringFormResult,
  processNewMonitoringFormResult,
  processNewMonitoringFormResultVersion,
  uploadResultFile,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsClient';
import {
  getMonitoringFormResultQueryKey,
  getMonitoringFormResultsForListQueryKey,
  getMonitoringFormResultsQueryKey,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsQuery';

export const useMonitoringFormFillingModal = () => {
  const queryClient = useQueryClient();
  const study = useStudy();
  const [resultId, setResultId] = useState<number | undefined>();
  const [configId, setConfigId] = useState<number | undefined>();

  const processAnswerHandler = useCallback(
    async (data: DataType, scoreResults: IScoreResult[], filesToCommit: string[]) => {
      if (!configId) throw new Error('Form configId is undefined');

      const fieldSkipReasons = null;
      await processNewMonitoringFormResult(
        new MonitoringFormProcessingDto({
          value: data,
          fieldSkipReasons: fieldSkipReasons,
          versionType: VersionType.Major,
          scoreResults: scoreResults.map((x) => new ScoreResult(x)),
          fileIdsToCommit: filesToCommit,
        }),
        configId,
      );
    },
    [configId],
  );

  const patchAnswerHandler = useCallback(
    async (id: number, data: DataType, scoreResults: IScoreResult[], filesToCommit: string[]) => {
      if (!configId) throw new Error('Form configId is undefined');
      if (!study?.id) throw new Error('Study id is undefined');

      const fieldSkipReasons = null;
      await processNewMonitoringFormResultVersion(
        id,
        new MonitoringFormProcessingDto({
          value: data,
          fieldSkipReasons: fieldSkipReasons,
          versionType: VersionType.Major,
          scoreResults: scoreResults.map((x) => new ScoreResult(x)),
          fileIdsToCommit: filesToCommit,
        }),
      );
    },
    [configId, study?.id],
  );

  const processAnswerFunc: ProcessAnswerFuncType = useCallback(
    async (data, scoreResults, fileIdsToCommit) => {
      if (isNil(resultId)) await processAnswerHandler(data, scoreResults, fileIdsToCommit);
      else await patchAnswerHandler(resultId!, data, scoreResults, fileIdsToCommit);

      await Promise.all(
        conditionalConcat(
          [
            queryClient.invalidateQueries(getMonitoringFormResultsQueryKey(configId!)),
            queryClient.invalidateQueries(getMonitoringFormResultsForListQueryKey(configId!)),
          ],
          !!resultId && [queryClient.invalidateQueries(getMonitoringFormResultQueryKey(resultId))],
        ),
      );
    },
    [configId, patchAnswerHandler, processAnswerHandler, queryClient, resultId],
  );

  const uploadFileFunc = useCallback(
    async (file: FileParameter) => {
      return uploadResultFile(study!.id, file);
    },
    [study],
  );

  const { modal, open } = useGeneralFormFillingModal({
    processAnswerFunc,
    uploadFileFunc,
  });

  const fill = useCallback(
    async (cId: number) => {
      setConfigId(cId);
      setResultId(undefined);

      try {
        const config = await getMonitoringFormConfig(cId);
        open({
          layout: config.layout,
          scoreSections: config.scoreSections ?? [],
          variables: config.variables ?? [],
          initialValue: undefined,
          resultId: undefined,
          modalTitle: config.title,
        });
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [open],
  );

  const edit = useCallback(
    async (resId: number, formConfigId: number) => {
      setConfigId(formConfigId);
      setResultId(resId);
      try {
        const config = await getMonitoringFormConfig(formConfigId);
        const result = await getMonitoringFormResult(resId);
        open({
          layout: config.layout,
          scoreSections: config.scoreSections ?? [],
          variables: config.variables ?? [],
          initialValue: result.value,
          resultId: result.formResultId,
          modalTitle: config.title,
        });
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [open],
  );

  return {
    modal,
    fill,
    edit,
  };
};
